import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import b64toBlob from 'b64-to-blob';
import CircularProgress from '@mui/material/CircularProgress';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { useAnalyticContext } from '../../../../hooks/analyticContext';
import { configureStackedBarChartForEdVisit } from '../../../ChartConfiguration/ChartConfiguration';
import ClaimsPerformance from '../../../../Models/Claims/ClaimsPreview.model';
import FilterOptions from '../FilterOptions';
import { exportAnalyticMembers, getEdVisitGraph, getLastClaimUpdatedOn } from '../AnalyticService';
import { ClaimAnalyticEnum } from '../../../../Enum/ClaimAnalyticEnum';
import { formatDate } from '../../../../utils/dateHelpers';

const EDVisit = () => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [graphSummary, setGraphSummary] = useState<ClaimsPerformance[]>([]);
    const [chartOptions, setChartOptions] = useState({});
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [lastUpdatedOn, setLastUpdatedOn] = useState('');

    const {
        edHealthPlanIds,
        setEdHealthPlanIds,
        edTeam,
        setEdTeam,
        showEdInActiveTeams,
        setShowEdInActiveTeams,
        edYears,
        setEdYears,
        edMonths,
        setEdMonths,
    } = useAnalyticContext();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartForEdVisit(
                    'ED Visit',
                    '',
                    '',
                    'Visit',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'EdVisit',
                    history
                )
            );
        }

        const data = await getLastClaimUpdatedOn();
        setLastUpdatedOn(data ? formatDate(new Date(`${data.split('T')[0]}T00:00:00`), 'MM/dd/yyyy') : '');
    }, [isFetched, setIsFetched, isGraphShow, history]);

    useEffect(() => {
        if (!isFetched && edHealthPlanIds && edHealthPlanIds.length > 0) {
            changeFilter();
            setIsFetched(true);
        }
        initialPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps;
    }, [edHealthPlanIds]);

    const changeFilter = async () => {
        if (edHealthPlanIds && edHealthPlanIds.length > 0) {
            setIsGraphShow(false);
            const data = await getEdVisitGraph(
                edHealthPlanIds,
                edTeam,
                edYears,
                edMonths,
                showEdInActiveTeams
            );
            setGraphSummary(data);
            setIsPrepared(true);
            setIsGraphShow(true);
        }

        setChartOptions(
            configureStackedBarChartForEdVisit(
                'ED Visit',
                '',
                '',
                'Visit',
                [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                [0],
                'EdVisit',
                history
            )
        );
    };

    useEffect(() => {
        if (graphSummary && isPrepared) {

            let xAxisValues = graphSummary.map(x => x.Category);

            let yAxisValues = [
                {
                    name: 'ED Visit',
                    data: graphSummary.map((x) => ({
                        y: x.TotalVisit
                    })),
                    color: '#6495ED',
                    showInLegend: false
                }
            ];

            setChartOptions(
                configureStackedBarChartForEdVisit(
                    'ED Visit',
                    '',
                    '',
                    'Visit',
                    yAxisValues,
                    xAxisValues,
                    'EdVisit',
                    history,
                )
            );

            setIsGraphShow(true);
            setIsPrepared(false);
        }
    }, [graphSummary, edHealthPlanIds, isPrepared, history]);

    const navigateToMembers = () => {
        history(`/provider/metricsummary/edvisitdetail`);
    };

    const handleDownload = async () => {
        try {
            setDownLoading(true);
            await exportDetail(); // your existing function
        } finally {
            setDownLoading(false);
        }
    };

    const exportDetail = async () => {
        const data = await exportAnalyticMembers(
            ClaimAnalyticEnum.EdVisit,
            edHealthPlanIds,
            edTeam,
            edYears,
            edMonths,
            showEdInActiveTeams
        );

        let typeName = 'EdVisit';
        let blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `${typeName} - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            Emergency Department Visits
                        </Typography>
                    </Grid>
                    <FilterOptions
                        healthPlanIds={edHealthPlanIds}
                        setHealthPlanIds={setEdHealthPlanIds}
                        years={edYears}
                        setYears={setEdYears}
                        months={edMonths}
                        setMonths={setEdMonths}
                        team={edTeam}
                        setTeam={setEdTeam}
                        showInActiveTeams={showEdInActiveTeams}
                        setShowInActiveTeams={setShowEdInActiveTeams}
                        onFetch={changeFilter}
                        claimType={ClaimAnalyticEnum.EdVisit}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Last claim updated: {lastUpdatedOn || 'N/A'}
                            </Typography>

                            <Box
                                onClick={!downLoading ? handleDownload : undefined}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    cursor: downLoading ? 'default' : 'pointer',
                                }}
                            >
                                {downLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <FileDownloadIcon
                                        sx={{
                                            color: 'black',
                                            fontSize: 24,
                                        }}
                                        titleAccess="Export"
                                    />
                                )}
                            </Box>
                        </Box>
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default EDVisit;
